// ============================================================================
// CONTRACT HTML RENDERER (ROTTERDAM) — renderContractHtmlNL.js
// Pure JavaScript — NO browser-only APIs. EUR currency, VAT, Dutch law / DNR 2025.
// Export: buildContractHtmlNL(contractData) → full HTML document string
// ============================================================================
const PMC_PARENT = 'Project Management Consultancy';
const PMC_SUBITEMS = [
  'Issue of Interim Certificate to the contractor(s) after checking the bills',
  'Taking final measurements and issuing final certificates after completion of works',
  'Bill of Quantities preparation, tender documents, Bill Checking',
  'Project and Time Management, cost management and day to day supervision',
];

const NL_OPTIONAL_BRIEF_POINTS = [
  'Use the natural potential of the site as far as possible by tying together functionality and creativity.',
  'Giving the house a relaxed feel by integrating the landscape with building and interior design. The house could merge with the landscape both physically and visually.',
  'The home must have a laid back and relaxed feel.',
  'Materials used in the home must to be locally sourced wherever possible, practical and easy to maintain/replace.',
];

const NL_DEFAULT_FEE_NOTES = [
  { id: 'n1', text: 'Our fees are only valid based on the terms of payment as per our Fee schedule and Terms and Conditions / Scope of Works document which are shared with this document.', enabled: true },
  { id: 'n2_area', text: '__area__', enabled: true },
  { id: 'n3', text: 'The bifurcated fees for Architectural Design and Interior Design are applicable only if we are commissioned for both services. If commissioned for either scope independently, the proposed fees will need to be revisited accordingly.', enabled: true },
  { id: 'n4', text: 'Fees based on the construction area are calculated considering all indoor, covered and semi-covered areas such as verandahs, porches, pergolas, pavilions and pools designed and coordinated by the design consultants.', enabled: true },
  { id: 'n5', text: 'In the event that the total constructed area exceeds the estimated area by more than 5%, the professional fee shall be recalculated on a pro-rata basis on the entire revised area at the agreed rate, subject to prior discussion and explicit approval from the Client.', enabled: true },
];

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/—/g, ' - ')
    .replace(/–/g, ' - ')
    .replace(/'/g, "'")
    .replace(/'/g, "'")
    .replace(/"/g, '"')
    .replace(/"/g, '"')
    .replace(/…/g, '...')
    .replace(/ /g, ' ')
    .replace(/�/g, '')
    .replace(/[￰-￿]/g, '');
}

function renderMarkup(text) {
  if (!text) return '';
  return text.split('\n').map(line => {
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map(part => {
      if (part.startsWith('**') && part.endsWith('**')) return `<strong>${escapeHtml(part.slice(2, -2))}</strong>`;
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return `<em>${escapeHtml(part.slice(1, -1))}</em>`;
      return escapeHtml(part);
    }).join('');
  }).join('<br/>');
}

function formatEUR(n) {
  if (n === null || n === undefined || n === '') return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return '-';
  return '&#8364; ' + num.toLocaleString('nl-NL', { maximumFractionDigits: 0 });
}

export const OFFICE_CONFIG_ROTTERDAM = {
  currency: 'EUR',
  formatCurrency: formatEUR,
  companyName: 'Architecture BRIO v.o.f.',
  companyAddress: 'Museumpark 25, 3015 CG Rotterdam, The Netherlands',
  taxLabel: 'VAT',
  defaultExpenses: `Reimbursable expenses are charged at actuals and are not included in the overall fee:

• Return business class flights for all work-related trips
• Work trips outside the project location: EUR 350 per person per day (applicable to BRIO principals/partners only; must be ratified and mutually agreed in advance)
• Accommodation and subsistence if required
• Incidental expenses: local transportation
• Presentation models, computer simulations, presentation drawings (if requested for purposes beyond design and execution)
• Material, construction and installation testing and analyses
• Up to 2 sets of drawings provided free; additional sets on a reimbursable basis`,
};

export function buildContractHtmlNL(contractData) {
  const {
    docType = 'offer',
    date = '',
    projectName = '',
    clientName = '',
    clientAddress = '',
    designBriefProjectType = '',
    designBriefLocation = '',
    designBriefCustomText = '',
    areaGroups = [],
    totalConstructedArea = 0,
    feeOfferGroups = [],
    totalDesignFee = 0,
    constructionCoordFee = 0,
    expensesText = '',
    paymentStages = [],
    validityMonths = 2,
    excludedServices = [],
    isAppointmentLetter = false,
    versionName = '',
    signatoryName = '',
    signatoryTitle = '',
    headerImageUrl = '',
    officeName = '',
    pdfStyles = {},
  } = contractData || {};

  const contractSettings = contractData?.contractSettings || {};
  const designBriefOptionalPoints = contractSettings.designBriefOptionalPoints ?? [0, 1, 2, 3];
  const designBriefCustomPoints = contractSettings.designBriefCustomPoints || [];
  const feeNotes = contractSettings.feeNotes || null;
  const areaUnit = contractSettings.areaUnit || 'sqft';
  const designValidityMonths = contractSettings.designValidityMonths ?? 6;
  const executionValidityMonths = contractSettings.executionValidityMonths ?? 24;
  const gracePeriodMonths = contractSettings.gracePeriodMonths ?? 1;
  const maxTimelineMonths = contractSettings.maxTimelineMonths ?? 33;
  const curtailmentPct = contractSettings.curtailmentPct ?? 10;
  const includeCurtailment = contractSettings.includeCurtailment ?? true;
  const includeTimelines = contractSettings.includeTimelines ?? true;
  const timelineStages = contractSettings.timelineStages || [];
  const timelineTotalLabel = contractSettings.timelineTotalLabel ?? 'of design stages';
  const timelineTotalDuration = contractSettings.timelineTotalDuration ?? '';
  const timelineTotalComment = contractSettings.timelineTotalComment ?? '';

  const cfg = OFFICE_CONFIG_ROTTERDAM;
  const fc = cfg.formatCurrency;
  const fontFamily = pdfStyles.fontFamily || 'Raleway';
  const primary = pdfStyles.primaryColor || '#9F902A';

  const apptLetter = isAppointmentLetter || docType === 'appointment_letter';

  // ── Area unit helpers ──
  const SQFT_PER_SQM = 10.7639;
  const cvArea = (v) => {
    const num = parseFloat(v);
    if (!num || num === 0) return '';
    return areaUnit === 'sqm' ? Math.round(num / SQFT_PER_SQM) : num;
  };
  const cvRate = (r) => areaUnit === 'sqm' ? r * SQFT_PER_SQM : r;
  const unitLabel = areaUnit === 'sqm' ? 'm<sup>2</sup>' : 'ft<sup>2</sup>';
  const unitLabelEsc = areaUnit === 'sqm' ? 'm²' : 'ft²';

  const headerHtml = headerImageUrl
    ? `<div class="header-img"><img src="${escapeHtml(headerImageUrl)}" crossorigin="anonymous" /></div>`
    : `<div class="header-text">BRIO - ${escapeHtml(officeName || cfg.companyName)}</div>`;

  // ── Preamble + Commission Documents (appointment letter only) ──
  let preambleSection = '';
  let commissionSection = '';
  if (apptLetter) {
    preambleSection = `
      <div class="preamble">
        <p>An agreement made this ${escapeHtml(date)} between:</p>
        <p><strong>${escapeHtml(clientName)}</strong>${clientAddress ? `<br/>${escapeHtml(clientAddress)}` : ''}, hereinafter called the "Client", of the one part,</p>
        <p>and <strong>${escapeHtml(cfg.companyName)}</strong><br/>${escapeHtml(cfg.companyAddress)}, hereinafter called the "Design Consultant", of the other part,</p>
        <p>declare to have agreed on the following.</p>
        <p>The Client herewith appoints the Design Consultant and the Design Consultant herewith declares to accept the commission of the following project: <strong>The Design of a ${escapeHtml(designBriefProjectType || projectName)}${designBriefLocation ? ` in ${escapeHtml(designBriefLocation)}` : ''}</strong>.</p>
      </div>`;
    commissionSection = `
      <div class="section">
        <h2>1. Commission Documents</h2>
        <p>The following commission documents determine the agreement, with their inherent rights and obligations, between the two parties:</p>
        <ul>
          <li>The appointment letter signed by both parties</li>
          <li>The appendixes initialed by both parties:
            <ul>
              <li>App. 1. Scope of Work</li>
              <li>App. 2. Terms and Conditions (incl. DNR 2025)</li>
            </ul>
          </li>
        </ul>
        <p>In case of contradictions between documents, the order of relevance is: (1) this appointment letter, (2) the scope of work, (3) the terms and conditions.</p>
      </div>`;
  }

  let chapterCounter = apptLetter ? 2 : 1;
  const nextCh = () => chapterCounter++;
  const n = () => nextCh();

  // ── Section: Design Brief ──
  const briefIntroSentence = apptLetter
    ? `Architecture BRIO is commissioned for the Design of a <strong>${escapeHtml(designBriefProjectType || '[project type]')}</strong> in <strong>${escapeHtml(designBriefLocation || '[location]')}</strong>. The scope of the project includes complete building design of the shell and core, of the home and interior design for the spaces within.`
    : `Architecture BRIO is pleased to present this consultancy offer for the Design of a <strong>${escapeHtml(designBriefProjectType || '[project type]')}</strong> in <strong>${escapeHtml(designBriefLocation || '[location]')}</strong>.`;

  const selectedOptionalPoints = NL_OPTIONAL_BRIEF_POINTS.filter((_, i) => designBriefOptionalPoints.includes(i));
  const allBriefPoints = [...selectedOptionalPoints, ...designBriefCustomPoints.filter(p => { const t = typeof p === 'string' ? p : (p && p.text) || ''; const en = typeof p === 'string' ? true : (p == null || p.enabled !== false); return en && t && t.trim(); }).map(p => typeof p === 'string' ? p : p.text)];
  const optionalPointsHtml = allBriefPoints.length > 0
    ? `<ol style="margin-top:0.25cm;">${allBriefPoints.map(p => `<li>${escapeHtml(p)}</li>`).join('')}</ol>`
    : '';

  const designBriefSection = `
    <div class="section">
      <h2>${n()}. Design Brief</h2>
      <p>${briefIntroSentence}</p>
      <p>We propose a comprehensive consultancy offer on the design of the project.</p>
      <p>Through research and design, a further investigation of the requirements and ambitions of the client will be made together with an analysis of the site and its potentials. Along with this, a detailed site analysis will lead to a programmatic area statement, first design proposal and possibly alternative proposals for the client to evaluate. Alternative development models will be explored in this initial study. Hereafter a concept design proposal will be prepared.</p>
      <p>The next phases will entail the making of a schematic and final design integrating all necessary engineering analysis advice and cost calculations. In these phases decisions can be made as well as how to organise the process of the actual construction of the project.</p>
      ${designBriefCustomText ? `<p style="margin-top:0.2cm;">${renderMarkup(designBriefCustomText)}</p>` : ''}
      ${optionalPointsHtml}
    </div>`;

  // ── Section: Area Program ──
  const areaRows = areaGroups.map(g => {
    const sub = (g.subrows || []).map(r => {
      const rawTotal = (parseFloat(r.indoor) || 0) + (parseFloat(r.covered_outdoor) || 0) + (parseFloat(r.non_covered_outdoor) || 0);
      return `
      <tr>
        <td>${escapeHtml(r.num)}</td>
        <td>${escapeHtml(r.label)}</td>
        <td class="num">${cvArea(r.indoor)}</td>
        <td class="num">${cvArea(r.covered_outdoor)}</td>
        <td class="num">${cvArea(r.non_covered_outdoor)}</td>
        <td class="num">${rawTotal > 0 ? cvArea(rawTotal) : ''}</td>
        <td></td>
      </tr>`;
    }).join('');
    const wcPct = g.wallsCirculationPct > 0 ? g.wallsCirculationPct + '%' : '';
    return `
      <tr class="group-row">
        <td>${escapeHtml(g.code)}</td>
        <td colspan="4"><strong>${escapeHtml(g.label)}</strong></td>
        <td class="num"><strong>${Number(g.constructedArea).toLocaleString('en-IN')}</strong></td>
        <td class="num">${escapeHtml(wcPct)}</td>
      </tr>
      ${sub}`;
  }).join('');

  const areaSection = `
    <div class="section" style="page-break-inside:auto;">
      <h2>${n()}. Project Description</h2>
      <p>The following programmatic area statement forms part of this offer. Areas are inclusive of all indoor, covered and semi-covered spaces (verandahs, porches, pergolas, pavilions, pools).</p>
      <table class="data-table area-table" style="margin-top:0.3cm;">
        <colgroup>
          <col style="width:7mm"/>
          <col style="width:68mm"/>
          <col style="width:21mm"/>
          <col style="width:21mm"/>
          <col style="width:21mm"/>
          <col style="width:20mm"/>
          <col style="width:12mm"/>
        </colgroup>
        <thead>
          <tr>
            <th>#</th><th>Space / Program</th><th class="num">Indoor (${unitLabel})</th>
            <th class="num">Covered Outdoor</th><th class="num">Non-covered Outdoor</th>
            <th class="num">Row Total</th><th class="num">W&amp;C %</th>
          </tr>
        </thead>
        <tbody>${areaRows || '<tr><td colspan="7" style="text-align:center;color:#999;">Area statement to be added</td></tr>'}</tbody>
        <tfoot style="display:table-row-group;">
          <tr><td colspan="5"><strong>Total Constructed Area</strong></td><td class="num"><strong>${Number(totalConstructedArea).toLocaleString('en-IN')} ${unitLabel}</strong></td><td></td></tr>
        </tfoot>
      </table>
    </div>`;

  // ── Section: Fees ──
  const feeRows = feeOfferGroups.map(g => `
    <tr>
      <td>${escapeHtml(g.label)}</td>
      <td class="num">${Number(g.constructedArea).toLocaleString('en-IN')} ${unitLabel}</td>
      <td class="num">${fc(Math.round(g.rate))} / ${unitLabel}</td>
      <td class="num">${fc(g.fee)}</td>
    </tr>`).join('');

  // Compute area breakdown note (note 2)
  const feeNoteAreaParts = areaGroups
    .filter(g => g.label && Number(g.constructedArea) > 0)
    .map(g => `${escapeHtml(g.label)}: ${Number(g.constructedArea).toLocaleString('en-IN')} ${unitLabelEsc}`);
  const feeNoteArea = feeNoteAreaParts.length > 0
    ? `The indicated total constructed area taken into consideration is ${feeNoteAreaParts.join(' and ')}.`
    : '';

  const ch3nl = nextCh();
  const feeSection = `
    <div class="section">
      <h2>${ch3nl}. Consultancy Costs</h2>
      <h3>${ch3nl}.1 Determination of Consultancy Costs</h3>
      <p>The consultancy cost of the Design Consultant consists of a combination of the following components:</p>

      <p style="margin-top:0.4cm;"><strong>1) Design Fee</strong></p>
      <p>Building Design and Interior Design: <strong>${fc(totalDesignFee)} + ${escapeHtml(cfg.taxLabel)}</strong></p>
      <table class="data-table" style="margin-top:0.25cm;">
        <thead>
          <tr><th>Space / Program</th><th class="num">Constructed Area (${unitLabel})</th><th class="num">Rate / ${unitLabel}</th><th class="num">Fees</th></tr>
        </thead>
        <tbody>${feeRows || '<tr><td colspan="4" style="text-align:center;color:#999;">Select a fee offer in the Offer tab</td></tr>'}</tbody>
        <tfoot>
          <tr><td colspan="3"><strong>Total Design Fee</strong></td><td class="num"><strong>${fc(totalDesignFee)}</strong></td></tr>
        </tfoot>
      </table>

      ${constructionCoordFee ? `
      <p style="margin-top:1.4cm;"><strong>2) Construction Coordination Fee</strong></p>
      <p style="font-weight:700;">${fc(constructionCoordFee)} + ${escapeHtml(cfg.taxLabel)} per month</p>
      <p class="note">Once the Construction Phase kicks off, a monthly retainer of ${fc(constructionCoordFee)} applies for the duration of the project for construction coordination.</p>
      ` : ''}

      <p class="note" style="margin-top:0.3cm;margin-bottom:0.15cm;">Fees will be subject to revision as per the following points.</p>
      <ol class="fee-notes">
        ${(feeNotes || NL_DEFAULT_FEE_NOTES).filter(note => note.enabled !== false).map(note => {
          const txt = note.text === '__area__' ? feeNoteArea : note.text;
          return txt ? `<li>${escapeHtml(txt)}</li>` : '';
        }).join('')}
      </ol>
    </div>`;

  // ── Section: Payment Schedule ──
  const stageRows = paymentStages.map(s => `
    <tr>
      <td>${escapeHtml(s.name)}</td>
      <td class="num">${escapeHtml(s.proportion)}</td>
      <td class="num">${escapeHtml(s.cumulative)}</td>
    </tr>`).join('');

  const ch4nl = nextCh();
  const paymentSection = `
    <div class="section">
      <h2>${ch4nl}. Fee Schedule &amp; Validity</h2>
      <p>Any tax levied by law (${escapeHtml(cfg.taxLabel)}) shall be payable by the Client above the gross fees stated. The Design Consultant will be paid in stages consistent with the progress of work. Part invoices may be submitted upon completion of deliverables within a particular stage.</p>

      ${stageRows ? `
      <table class="data-table" style="margin-top:0.4cm;">
        <thead><tr><th>Stage</th><th class="num">Proportion</th><th class="num">Cumulative</th></tr></thead>
        <tbody>${stageRows}</tbody>
      </table>` : ''}

      <h3>${ch4nl}.1 Validity of Fees and Escalation</h3>
      <ol>
        <li>The proposal is valid for ${escapeHtml(String(validityMonths))} months from the date of submission of this Offer.</li>
        <li>For validity of rates we have considered an overall duration of a maximum of ${escapeHtml(String(designValidityMonths))} months towards design stages and a maximum of ${escapeHtml(String(executionValidityMonths))} months during execution stages. We will consider a grace period of ${escapeHtml(String(gracePeriodMonths))} months, however additional fees will be applicable if the total project timeline exceeds ${escapeHtml(String(maxTimelineMonths))} months at a rate mutually agreed between Client and Design Consultant.</li>
        <li>Any variation in the brief after approval of Stage 2 (Schematic Design), either in Scope of Work or Scope of Services such as a change of brief, revisions due to building approvals, the addition of extra areas or extra zones not originally included, or design changes by other parties that result in revising layouts or details, which will increase the amount of work or change the scope of work originally described and authorised by the Client, will be charged on a time charge basis at costs mutually agreed between Client and Design Consultant.</li>
        <li>All GFC drawings once submitted will have to be approved by the Client. Revision in drawings hereafter will be charged additionally.</li>
        <li>The Client, their representative, or the executing party, will not be allowed to make any changes during the execution of the project without prior approval by the Design Consultant.</li>
        <li>All drawings by external consultants will have to be submitted on a timely basis to the Design Consultant for approval.</li>
        ${includeCurtailment ? `<li>Any curtailment of the professional services, after approval of Schematic Design, shall make it obligatory for the Client to pay at least ${escapeHtml(String(curtailmentPct))}% of the fee for the remaining Stage(s) of the curtailed work/services.</li>` : ''}
      </ol>

      <h3>${ch4nl}.2 Expenses</h3>
      <p>The expenses of the Design Consultant, Principal Consultant and/or his representative, for the benefit of the commission shall be paid on actuals towards the following:</p>
      <ol>
        <li>Return economy class airfare for all domestic flights are to be paid on actuals and are not included in the overall fee.</li>
        <li>If there are work related trips outside of the Netherlands that are ratified and mutually agreed to in advance then the business class flights are to be paid on actuals. In addition to this a daily charge of ${escapeHtml((() => { const raw = (expensesText || '').trim(); const n = parseFloat(raw.replace(/,/g, '')); return (!isNaN(n) && raw !== '') ? 'EUR ' + Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : raw || 'EUR 350'; })())} per person per day from the date of the travel will be applicable. This charge is applicable for the principals/partners of BRIO only.</li>
        <li>Accommodation and subsistence expenses if required.</li>
        <li>Incidental expenses such as local transportation.</li>
        <li>Cost of presentation models, computer simulation, presentation drawings, etc., prepared at the insistence of the Client for purposes other than the Design and execution of the project.</li>
        <li>The expenses connected with the testing of materials, constructions and installations, as well as the expenses connected with other simple tests and analyses.</li>
        <li>The Design Consultant shall supply to the Client, free of cost, up to two sets of drawings, documents and contracts at different stages. Additional sets will be on a reimbursable basis.</li>
      </ol>
    </div>`;

  // ── Section: Project Timelines (optional) ──
  let timelinesSection = '';
  if (includeTimelines) {
    const tlRows = timelineStages.map((s, i) => `
      <tr>
        <td>${i + 1}. ${escapeHtml(s.name || '')}</td>
        <td class="num">${escapeHtml(String(s.duration || ''))}</td>
        <td>${escapeHtml(s.comment || '')}</td>
      </tr>`).join('');
    timelinesSection = `
    <div class="section">
      <h2>${n()}. Project Timelines</h2>
      <p>The following timeline is indicative and subject to change based on project progress and client approvals.</p>
      ${tlRows ? `
      <table class="data-table" style="margin-top:0.3cm;">
        <colgroup><col style="width:50%"/><col style="width:20%"/><col style="width:30%"/></colgroup>
        <thead><tr><th>Stage</th><th class="num">Duration (months)</th><th>Comments</th></tr></thead>
        <tbody>${tlRows}</tbody>
        <tfoot><tr>
          <td><strong>Total ${escapeHtml(timelineTotalLabel)}</strong></td>
          <td class="num"><strong>${escapeHtml(String(timelineTotalDuration))}</strong></td>
          <td>${escapeHtml(timelineTotalComment)}</td>
        </tr></tfoot>
      </table>
      <p class="note" style="margin-top:0.25cm;">The proposed timeline is subject to feedback loops, the decision making process, scheduled meetings/zoom meetings needed for the process, decisions and clearances from the liaison architect and appointed consultants for the decision process to follow with each milestone. The Interior Design stage may not follow exactly in the same order as proposed above depending on various decision making factors. The construction timeline is further dependent on the chosen construction methodology, availability of materials, site readiness, contractor coordination, and unforeseen site or execution-related constraints. Variations in any of these factors may require adjustments to the projected schedule.</p>
      ` : '<p style="color:#999;font-style:italic;">Timeline to be added.</p>'}
    </div>`;
  }

  // ── Section: Excluded Services ──
  const topLevelExcluded = (excludedServices || []).filter(s => !PMC_SUBITEMS.includes(s));
  let itemNum = 0;
  const excludedItemsHtml = topLevelExcluded.map(svc => {
    itemNum++;
    if (svc === PMC_PARENT) {
      const subItems = PMC_SUBITEMS.filter(sub => (excludedServices || []).includes(sub));
      const subHtml = subItems.length > 0
        ? `<ol style="padding-left:0;margin-top:0.1cm;margin-bottom:0;list-style-type:none;">${subItems.map((sub, si) => `<li style="padding-left:0.5cm;">${itemNum}.${si + 1} ${escapeHtml(sub)}</li>`).join('')}</ol>`
        : '';
      return `<li>${escapeHtml(svc)}${subHtml}</li>`;
    }
    return `<li>${escapeHtml(svc)}</li>`;
  }).join('');

  const excludedSection = `
    <div class="section">
      <h2>${n()}. Services Excluded</h2>
      <p>Excluded from this calculation is the cost of the work relating to the services listed below as services excluded.</p>
      <ol>${excludedItemsHtml || '<li style="color:#999;">No services excluded</li>'}</ol>
    </div>`;

  // ── Section: Communication ──
  const communicationSection = `
    <div class="section">
      <h2>${n()}. Communication and Provisions of Information</h2>
      <p>The Client shall provide location plan, measured drawings and photographs of existing photos of the site. In case such information is not readily available, the Client shall arrange for the collection of necessary information and pay for the same.</p>
      <p>The Client shall furnish specific conditions / statutory stipulations / codes of practice / schedule of rates, etc. desired to be followed.</p>
    </div>`;

  // ── Rotterdam Legal Sections ──
  const legalSections = `
    <div class="section">
      <h2>${n()}. Order of Precedence</h2>
      <p>In the event of contradictions between documents, the following order of precedence applies:</p>
      <ol>
        <li>This offer and its conditions</li>
        <li>The appendices (scope of services, fee and payment, programme)</li>
        <li>DNR 2025</li>
      </ol>
    </div>
    <div class="section">
      <h2>${n()}. Governing Law</h2>
      <p>This agreement shall be governed by and construed in accordance with the laws of the Netherlands.</p>
    </div>
    <div class="section">
      <h2>${n()}. Applicable General Conditions (DNR 2025)</h2>
      <p>De Nieuwe Regeling 2025 (DNR 2025), the standard conditions published by BNA and Koninklijke NLingenieurs, apply to this agreement and are provided as an attachment. In the event of conflict between this agreement and DNR 2025, the provisions of this agreement shall prevail.</p>
      <p class="note">Note: DNR 2025 is applicable only when it has been explicitly accepted by the Client and a copy has been provided prior to or at the time of contract conclusion, in accordance with Article 6:234 of the Dutch Civil Code (BW).</p>
    </div>
    <div class="section">
      <h2>${n()}. Dispute Resolution</h2>
      <p>Parties shall first attempt to resolve any dispute amicably. If no amicable resolution is reached, the dispute shall be submitted to arbitration under the rules of the Netherlands Arbitration Institute (NAI), with the seat of arbitration in Amsterdam, Netherlands, conducted in the English language, before one arbitrator.</p>
    </div>
    <div class="section">
      <h2>${n()}. Limitation of Liability</h2>
      <p>The total aggregate liability of the Design Consultant under or in connection with this agreement, whether in contract, tort (including negligence) or otherwise, shall not exceed EUR 50,000. The Design Consultant shall not be liable for any indirect or consequential loss. The exclusions and limitations set out in DNR 2025 also apply.</p>
    </div>
    <div class="section">
      <h2>${n()}. Scope</h2>
      <p>The services of the Design Consultant under this agreement are limited to those described herein and in the attached scope of services. The Design Consultant is not responsible for detailed design, technical design, execution design, construction, supervision, cost control, or any adaptation or use of the concept by the Client or third parties beyond the agreed scope. Any such use is at the sole risk of the Client.</p>
    </div>`;

  // ── End Clauses ──
  const endSection = `
    <div class="section">
      <h2>${n()}. Subjects Yet to be Determined</h2>
      <p>The following items are yet to be agreed and will be discussed as they become available:</p>
      <ol>
        <li>Detailed Programmatic Area Statement</li>
        <li>Project Budget</li>
      </ol>
      ${apptLetter ? '' : '<p style="margin-top:0.4cm;" class="note">This constitutes an initial proposal for the project. The Standard Scope of Works and Terms &amp; Conditions (including DNR 2025) follow as separate appendices.</p>'}
    </div>`;

  // ── Signature block (appointment letter only) ──
  const signatureSection = apptLetter ? `
    <div class="section signature-block">
      <p style="margin-bottom:0.3cm;">as agreed on</p>
      <div class="sign-grid">
        <div class="sign-col">
          <p style="margin-bottom:0.15cm;">Date:<br/>Place:</p>
          <div class="sign-line" style="margin-top:2.5cm;"></div>
          <p style="margin-bottom:0.05cm;"><strong>For the Client</strong></p>
          <p style="margin-bottom:0.05cm;">${escapeHtml(clientName)}</p>
          ${clientAddress ? `<p style="font-size:9pt;color:#666;margin-bottom:0.05cm;">${escapeHtml(clientAddress)}</p>` : ''}
        </div>
        <div class="sign-col">
          <p style="margin-bottom:0.15cm;">Date: ${escapeHtml(date)}<br/>Place: Rotterdam</p>
          <div class="sign-line" style="margin-top:2.5cm;"></div>
          <p style="margin-bottom:0.05cm;"><strong>For the Design Consultant</strong></p>
          <p style="margin-bottom:0.05cm;"><strong>${escapeHtml(signatoryName)}</strong></p>
          <p style="margin-bottom:0.05cm;">${escapeHtml(signatoryTitle)}</p>
          <p style="margin-bottom:0.05cm;">${escapeHtml(cfg.companyName)}</p>
          <p style="font-size:9pt;color:#666;">${escapeHtml(cfg.companyAddress)}</p>
        </div>
      </div>
    </div>` : '';

  const docTitle = apptLetter ? 'APPOINTMENT LETTER' : 'CONSULTANCY OFFER';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>${escapeHtml(docTitle)} - ${escapeHtml(projectName)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com"/>
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
  <link href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&display=swap" rel="stylesheet"/>
  <style>
* { box-sizing: border-box; margin: 0; padding: 0; }
@page { size: A4; margin-top: 25mm; }
@page :first { margin-top: 0; }
html, body { width: 210mm; min-height: 297mm; margin: 0; padding: 0; }
body {
  font-family: '${escapeHtml(fontFamily)}', 'Century Gothic', system-ui, sans-serif;
  font-size: 9pt; line-height: 1.45; color: #222; font-weight: 300;
  display: flex; flex-direction: column;
}
.header-img { width: 100%; flex-shrink: 0; }
.header-img img { width: 100%; height: auto; display: block; }
.header-text {
  font-size: 15px; font-weight: 300; letter-spacing: 0.06em; color: ${primary};
  padding: 18px 25mm 10px; border-bottom: 1px solid #ddd;
}
.main-content { padding: 0.7cm 0 0 0; flex: 1; }
h1 { font-size: 13pt; font-weight: 700; color: ${primary}; margin-bottom: 0.15cm; letter-spacing: 0.04em; text-transform: uppercase; }
.subtitle { font-size: 9pt; color: #555; margin-bottom: 0.5cm; font-weight: 300; }
h2 { font-size: 9.5pt; font-weight: 600; color: #222; margin-bottom: 0.4cm; padding-bottom: 0.15cm; border-bottom: 1px solid ${primary}; margin-top: 0.9cm; text-transform: uppercase; }
h3 { font-size: 9pt; font-weight: 600; color: #222; margin-bottom: 0.12cm; margin-top: 0.9cm; }
.section { margin-bottom: 1.4cm; page-break-inside: avoid; }
p { margin-top: 0; margin-bottom: 0.9cm; }
.pre-text { white-space: pre-wrap; font-size: 9pt; line-height: 1.45; margin-bottom: 0.9cm; }
.preamble { margin-bottom: 1.4cm; line-height: 1.45; }
.note { font-size: 8.5pt; font-style: italic; color: #555; margin-top: 0.15cm; margin-bottom: 0.15cm; }
ul, ol { padding-left: 1cm; margin-bottom: 0.35cm; }
li { margin-bottom: 0.35cm; }
table.data-table { width: 100%; border-collapse: collapse; margin: 0.15cm 0; font-size: 9pt; }
table.data-table thead { display: table-header-group; }
table.data-table th { text-align: left; border-bottom: 1px solid ${primary}; padding: 3px 5px; color: #222; font-weight: 600; font-size: 8.5pt; }
table.data-table th.num { text-align: right; }
table.data-table td { padding: 2px 5px; border-bottom: 1px solid #eee; vertical-align: top; }
table.data-table tr { page-break-inside: avoid; }
table.data-table tfoot td { border-top: 1px solid #ccc; border-bottom: none; font-weight: 600; }
.num { text-align: right; }
.area-table { font-size: 7.5pt; }
.area-table th { font-size: 7.5pt; padding: 2px 4px; }
.area-table td { padding: 1px 4px; }
ol.fee-notes { font-size: 8.5pt; font-style: italic; color: #555; padding-left: 1cm; margin-top: 0.2cm; margin-bottom: 0.35cm; }
ol.fee-notes li { margin-bottom: 0.12cm; }
.group-row td { background: #faf7e8; font-weight: 600; }
.signature-block { margin-top: 1cm; page-break-inside: avoid; }
.sign-grid { display: flex; gap: 2cm; margin-top: 0.7cm; }
.sign-col { flex: 1; }
.sign-line { border-top: 1px solid #333; margin-bottom: 0.2cm; height: 1cm; }
.sign-col p { font-size: 9pt; margin-bottom: 0.1cm; }
@media print { .section { page-break-inside: avoid; } }
  </style>
</head>
<body>
  ${headerHtml}
  <div class="main-content" style="padding: 0.8cm 20mm 0 20mm;">
    <div style="display:flex;gap:1.5cm;align-items:flex-start;margin-bottom:0.5cm;">
      <div>
        <h1>${escapeHtml(docTitle)}${versionName ? ` <span style="font-size:9pt;font-weight:400;letter-spacing:0;text-transform:none;color:#777;">${escapeHtml(versionName)}</span>` : ''}</h1>
        <div class="subtitle">Rotterdam, ${escapeHtml(date)}</div>
      </div>
      ${(designBriefProjectType || designBriefLocation) ? `
      <div style="border-left:1px solid #ddd;padding-left:1.5cm;">
        <h1>${escapeHtml([designBriefProjectType, designBriefLocation ? 'in ' + designBriefLocation : ''].filter(Boolean).join(' '))}</h1>
      </div>` : ''}
    </div>
    ${preambleSection}
    ${commissionSection}
    ${designBriefSection}
    ${areaSection}
    ${feeSection}
    ${paymentSection}
    ${timelinesSection}
    ${excludedSection}
    ${communicationSection}
    ${legalSections}
    ${endSection}
    ${signatureSection}
  </div>
</body>
</html>`;
}

export default buildContractHtmlNL;
